const API_BASE = import.meta.env.VITE_API_URL || "";

import type {
  BacktestResult,
  TodayAllocation,
  TradeOrder,
} from "../stores/backtestStore";
import { useSessionExpiredStore } from "../stores/sessionExpiredStore";

// Re-export for convenience
export type { BacktestResult, TodayAllocation, TradeOrder };

interface ApiError {
  error: string;
}

// Generate a correlation ID for request tracing
function generateCorrelationId(): string {
  // Use crypto.randomUUID if available (modern browsers), fallback to timestamp-based
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback: timestamp + random suffix
  return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 11)}`;
}

// Logging utility for API calls
const LOG_ENABLED = true; // Can be controlled via env var if needed

function logApi(
  method: string,
  endpoint: string,
  phase: "request" | "response" | "error",
  details: Record<string, unknown>,
  startTime?: number,
) {
  if (!LOG_ENABLED) return;
  
  const elapsed = startTime ? `${(performance.now() - startTime).toFixed(0)}ms` : "";
  const prefix = `[API ${method}]`;
  const style = phase === "error" 
    ? "color: #ef4444; font-weight: bold" 
    : phase === "response" 
      ? "color: #22c55e" 
      : "color: #3b82f6";
  
  console.groupCollapsed(
    `%c${prefix} ${endpoint} ${phase.toUpperCase()} ${elapsed}`,
    style,
  );
  
  Object.entries(details).forEach(([key, value]) => {
    if (key === "payload" && typeof value === "object") {
      // Truncate large payloads for logging
      const payload = value as Record<string, unknown>;
      const loggablePayload = { ...payload };
      if (typeof loggablePayload.strategyCode === "string") {
        loggablePayload.strategyCode = `[${(loggablePayload.strategyCode as string).length} chars]`;
      }
      console.log(`${key}:`, loggablePayload);
    } else {
      console.log(`${key}:`, value);
    }
  });
  
  console.groupEnd();
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  timeoutMs: number = 300000, // 5 minute default timeout
): Promise<T> {
  const startTime = performance.now();
  const method = options.method || "GET";
  
  // Generate correlation ID for request tracing
  const correlationId = generateCorrelationId();

  // Log request
  let payloadSize = 0;
  let payloadPreview: Record<string, unknown> = {};
  if (options.body) {
    payloadSize = (options.body as string).length;
    try {
      payloadPreview = JSON.parse(options.body as string);
    } catch {
      payloadPreview = { raw: options.body };
    }
  }
  
  logApi(method, endpoint, "request", {
    correlationId,
    payloadSize: `${(payloadSize / 1024).toFixed(1)}KB`,
    payload: payloadPreview,
    timeout: `${timeoutMs}ms`,
  });

  const token = localStorage.getItem("tradecraft-auth")
    ? JSON.parse(localStorage.getItem("tradecraft-auth")!).state?.token
    : null;

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    "X-Correlation-ID": correlationId,
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Try to parse JSON, but handle cases where response isn't JSON
    let data: unknown;
    try {
      data = await response.json();
    } catch (parseError) {
      // If we can't parse JSON and status is 401, still trigger session expired
      if (response.status === 401) {
        console.warn("[API] 401 Unauthorized (no JSON) - triggering session expired modal");
        useSessionExpiredStore.getState().setExpired();
      }
      
      logApi(method, endpoint, "error", {
        status: response.status,
        error: "Invalid JSON response",
      }, startTime);
      throw new Error("Server returned invalid response");
    }

    if (!response.ok) {
      const error = (data as ApiError).error || "Request failed";
      
      // Handle 401 Unauthorized - session expired
      if (response.status === 401) {
        console.warn("[API] 401 Unauthorized - triggering session expired modal");
        useSessionExpiredStore.getState().setExpired(
          error === "Unauthorized" || error === "Request failed"
            ? "Your session has expired. Please log in again to continue."
            : error
        );
      }
      
      logApi(method, endpoint, "error", {
        status: response.status,
        error,
      }, startTime);
      throw new Error(error);
    }

    // Log successful response
    logApi(method, endpoint, "response", {
      status: response.status,
      success: (data as Record<string, unknown>).success ?? true,
      dataKeys: Object.keys(data as object),
    }, startTime);

    return data as T;
  } catch (error) {
    clearTimeout(timeoutId);
    
    const errorMessage = error instanceof Error 
      ? (error.name === "AbortError" ? "Request timed out" : error.message)
      : "Unknown error";
    
    logApi(method, endpoint, "error", {
      error: errorMessage,
    }, startTime);
    
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error("Request timed out. Try a shorter backtest period.");
    }
    throw error;
  }
}

// Backtest APIs - BacktestResult is the API response type (imported from store)
export interface BacktestOptions {
  strategyCode: string;
  days?: number;
  useCache?: boolean;
  useFastMode?: boolean;
}

export async function runBacktest(
  strategyCode: string,
  days: number = 60,
  options: {
    useCache?: boolean;
    useFastMode?: boolean;
    strategyId?: string;
    startDate?: string;
    endDate?: string;
    initialCapital?: number;
    slippageBps?: number;
    includeRegulatoryFees?: boolean;
    benchmarkTicker?: string;
    wholeSharesOnly?: boolean;
  } = {},
): Promise<BacktestResult> {
  const {
    useCache = false,
    useFastMode = true,
    strategyId,
    startDate,
    endDate,
    initialCapital = 100000,
    slippageBps = 1,
    includeRegulatoryFees = true,
    benchmarkTicker,
    wholeSharesOnly = false,
  } = options;

  // Log backtest mode for debugging
  const mode = strategyId ? "strategy" : "build";
  console.log(
    `%c[BACKTEST] Mode: ${mode.toUpperCase()}`,
    "color: #f59e0b; font-weight: bold",
    {
      strategyId: strategyId || "(none)",
      codeLength: strategyCode?.length || 0,
      days,
      startDate,
      endDate,
      useCache,
      useFastMode,
    },
  );

  // Build request body based on what's provided:
  // - strategyId: fetch code from DB (most efficient for community/bookmarked strategies)
  // - strategyCode: send code directly (for custom strategies)
  const body: Record<string, unknown> = {
    // Only send days if no custom dates
    ...(startDate && endDate ? {} : { days }),
    useCache,
    useFastMode,
    initialCapital,
    slippageBps,
    includeRegulatoryFees,
    ...(wholeSharesOnly && { wholeSharesOnly }),
    ...(startDate && { startDate }),
    ...(endDate && { endDate }),
    ...(benchmarkTicker && { benchmarkTicker }),
  };

  // Priority: strategyId > strategyCode
  if (strategyId) {
    body.strategyId = strategyId;
    console.log(
      "%c[BACKTEST] Using strategyId - no code upload",
      "color: #22c55e",
    );
  } else {
    body.strategyCode = strategyCode;
    console.log(
      `%c[BACKTEST] Uploading strategy code: ${strategyCode.length} chars`,
      "color: #f59e0b",
    );
  }

  return request<BacktestResult>("/api/backtest/run", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface AllocationResponse {
  date: string;
  allocation: Record<string, { weight: number; dollarAmount: number }>;
  portfolioValue: number;
}

export async function getTodayAllocation(
  strategyCode: string,
  portfolioValue: number = 100000,
): Promise<AllocationResponse> {
  return request<AllocationResponse>("/api/allocations/today", {
    method: "POST",
    body: JSON.stringify({ strategyCode, portfolioValue }),
  });
}

// Strategies APIs
export interface Strategy {
  id: string;
  name: string;
  description: string;
  folder?: string;
  cagr: number;
  sharpe: number;
  maxDrawdown: number;
  status: string;
  code?: string;
  hidden?: boolean;
  source?: "system" | "community";
  tags?: string[];
  submittedAt?: string;
}

export interface StrategyMetricsInput {
  cagr: number;
  sharpe: number;
  sortino: number;
  maxDrawdown: number;
  volatility: number;
  calmar?: number;
  bestYear: number;
  worstYear: number;
  totalReturn: number;
  tradingDays: number;
  trailing1m?: number;
  trailing3m?: number;
  alpha?: number;
  beta?: number;
  rSquared?: number;
  correlation?: number;
}

export interface SaveStrategyRequest {
  name: string;
  description: string;
  tags: string[];
  code: string;
  metrics: StrategyMetricsInput;
  is_community?: boolean;
}

export async function getStrategies(): Promise<{ strategies: Strategy[] }> {
  return request("/api/strategies");
}

export async function getStrategyById(
  id: string,
): Promise<{ success: boolean; strategy: Strategy }> {
  return request(`/api/strategies/${encodeURIComponent(id)}`);
}

export async function saveStrategy(
  strategy: SaveStrategyRequest,
): Promise<{ success: boolean; strategy: Strategy }> {
  return request("/api/strategies/community", {
    method: "POST",
    body: JSON.stringify(strategy),
  });
}

export async function deleteStrategy(
  id: string,
): Promise<{ success: boolean }> {
  return request(`/api/strategies/community?id=${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}
